import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/lib/property";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ForkKnifeIcon, PackageIcon, ReceiptIcon, WarningIcon, PrinterIcon, HouseIcon, TableIcon, QrCodeIcon, MagnifyingGlassIcon, TrashIcon } from "@phosphor-icons/react";
import { PosCart } from "./pos/PosCart";
import { MenuManager } from "./pos/MenuManager";
import { StockManager } from "./pos/StockManager";
import { TablesFloor } from "./pos/TablesFloor";
import { TableOrderPanel } from "./pos/TableOrderPanel";
import { TableHistory } from "./pos/TableHistory";
import { MenuQR } from "./pos/MenuQR";
import { inr, CATEGORY_ICON, CATEGORY_TONE, isOutOfStock, remainingPegs } from "./pos/posHelpers";

export default function POS() {
  const { activeId, active } = useProperty();
  const { user } = useAuth();
  const [tab, setTab] = useState("tables");
  const [menu, setMenu] = useState([]);
  const [cart, setCart] = useState([]); // [{menu_item, qty}]
  const [tableNo, setTableNo] = useState("");
  const [customer, setCustomer] = useState("");
  const [paymentMode, setPaymentMode] = useState("cash");
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState("amount");
  const [orderType, setOrderType] = useState("dine_in");
  const [occupiedRooms, setOccupiedRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState("");
  const [selectedTableOrderId, setSelectedTableOrderId] = useState("");
  const [openTables, setOpenTables] = useState([]);
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({ orders_count: 0, gross_total: 0, gst_total: 0, by_payment: {}, low_stock: [] });
  const [catFilter, setCatFilter] = useState("all");
  const [orderSearch, setOrderSearch] = useState("");
  const [activeTable, setActiveTable] = useState(null); // open table_order object
  const [tablesRefreshKey, setTablesRefreshKey] = useState(0);
  const [platform, setPlatform] = useState(null);
  const canManage = user?.role === "admin" || user?.role === "manager";
  const canDeleteBills = user?.role === "admin";

  const deleteOrder = async (o) => {
    if (!window.confirm(`Void POS order ${o.order_no}?\n\nStock will be restored. This cannot be undone.`)) return;
    try {
      await api.delete(`/pos/orders/${o.id}`);
      toast.success(`Order ${o.order_no} voided`);
      loadOrders(); loadSummary(); loadMenu();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  useEffect(() => { api.get("/settings/platform").then((r) => setPlatform(r.data)).catch(() => {}); }, []);

  const loadMenu = () => activeId && api.get("/pos/menu", { params: { property_id: activeId } }).then((r) => setMenu(r.data));
  const loadOrders = () => activeId && api.get("/pos/orders", { params: { property_id: activeId, day: new Date().toISOString().slice(0, 10) } }).then((r) => setOrders(r.data));
  const loadSummary = () => activeId && api.get("/pos/summary", { params: { property_id: activeId } }).then((r) => setSummary(r.data));
  const loadOccupied = () => activeId && api.get("/pos/occupied-rooms", { params: { property_id: activeId } }).then((r) => setOccupiedRooms(r.data)).catch(() => setOccupiedRooms([]));
  const loadOpenTables = () => activeId && api.get("/tables/status", { params: { property_id: activeId } })
    .then((r) => setOpenTables((r.data || []).filter((t) => t.open_order).map((t) => ({ ...t.open_order, table_number: t.number, section: t.section }))))
    .catch(() => setOpenTables([]));

  useEffect(() => { loadMenu(); loadOrders(); loadSummary(); loadOccupied(); loadOpenTables(); /* eslint-disable-next-line */ }, [activeId]);

  useEffect(() => {
    if (orderType === "room_service") setPaymentMode("room_charge");
  }, [orderType]);

  const addToCart = (mi) => {
    if (isOutOfStock(mi)) return toast.error(`${mi.name} is out of stock`);
    setCart((c) => {
      const ex = c.find((x) => x.menu_item.id === mi.id);
      if (ex) return c.map((x) => x.menu_item.id === mi.id ? { ...x, qty: x.qty + 1 } : x);
      return [...c, { menu_item: mi, qty: 1 }];
    });
  };
  const updateQty = (id, delta) =>
    setCart((c) => c.map((x) => x.menu_item.id === id ? { ...x, qty: Math.max(0, x.qty + delta) } : x).filter((x) => x.qty > 0));
  const removeLine = (id) => setCart((c) => c.filter((x) => x.menu_item.id !== id));

  const totals = useMemo(() => {
    let sub = 0, tax = 0;
    cart.forEach((c) => {
      const s = c.menu_item.price * c.qty;
      sub += s;
      tax += s * c.menu_item.gst_rate / 100;
    });
    const gross = sub + tax;
    const discountValue = discountType === "percent"
      ? Math.max(0, Math.min(100, Number(discount) || 0)) * gross / 100
      : Number(discount) || 0;
    const grand = Math.max(0, gross - discountValue);
    return { sub: +sub.toFixed(2), tax: +tax.toFixed(2), grand: +grand.toFixed(2), discountValue: +discountValue.toFixed(2) };
  }, [cart, discount, discountType]);

  const submitOrder = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    const isRoom = orderType === "room_service" || paymentMode === "room_charge";
    const isTableRound = orderType === "dine_in" && !!selectedTableOrderId;
    if (isRoom && !selectedRoomId) return toast.error("Pick a room for room service / room charge");
    const res = occupiedRooms.find((r) => r.room_id === selectedRoomId);
    if (isRoom && res && !res.is_checked_in) {
      return toast.error(`Room ${res.room_number} is not currently checked-in — cannot post to room. Pick a checked-in room or switch payment mode to cash / UPI / card.`);
    }
    try {
      const r = await api.post("/pos/orders", {
        property_id: activeId,
        table_no: tableNo || null,
        customer_name: customer || (res ? res.guest_name : null),
        items: cart.map((c) => ({
          menu_item_id: c.menu_item.id,
          name: c.menu_item.unit_type === "peg"
            ? `${c.menu_item.name} (${c.menu_item.peg_size_ml}ml peg)`
            : c.menu_item.name,
          price: c.menu_item.price,
          gst_rate: c.menu_item.gst_rate,
          qty: c.qty,
        })),
        payment_mode: isTableRound ? "cash" : paymentMode,
        discount: isTableRound ? 0 : totals.discountValue,
        order_type: orderType,
        room_id: isRoom && res ? res.room_id : null,
        guest_id: isRoom && res ? res.guest_id : null,
        table_order_id: isTableRound ? selectedTableOrderId : null,
      });
      const msg = isTableRound
        ? `Round ${r.data.order_no} added to Table ${openTables.find((t) => t.id === selectedTableOrderId)?.table_number} · will settle when the table closes`
        : r.data.billed_invoice_id
        ? `Order ${r.data.order_no} · ${inr(r.data.total)} · appended to open invoice`
        : isRoom
        ? `Order ${r.data.order_no} · ${inr(r.data.total)} · will bill at check-out`
        : `Order ${r.data.order_no} · ${inr(r.data.total)}`;
      toast.success(msg);
      setCart([]); setTableNo(""); setCustomer(""); setDiscount(0); setSelectedRoomId(""); setSelectedTableOrderId("");
      loadMenu(); loadOrders(); loadSummary(); loadOpenTables();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filteredMenu = (catFilter === "all" ? menu : menu.filter((m) => m.category === catFilter))
    .filter((m) => {
      const q = orderSearch.trim().toLowerCase();
      if (!q) return true;
      return (m.name || "").toLowerCase().includes(q) || (m.sku || "").toLowerCase().includes(q) || (m.category || "").toLowerCase().includes(q);
    });
  const categories = ["all", ...Array.from(new Set(menu.map((m) => m.category)))];

  return (
    <div>
      <PageHeader
        overline={active ? `Restaurant & Bar · ${active.name}` : "Restaurant & Bar"}
        title={active ? `POS · ${active.name}` : "POS"}
        description="Ring up food, bar and beverage orders. GST is per-item, stock auto-decrements on sale. Switch the sidebar's property to open its own POS."
      />

      <Card className="p-4 mb-4 bg-primary/5 border-primary/20 flex flex-wrap items-center gap-3" data-testid="pos-property-banner">
        <HouseIcon size={20} weight="duotone" className="text-primary" />
        <div className="flex-1 min-w-[200px]">
          <p className="label-overline">POS for property</p>
          <p className="font-display text-xl leading-tight">{active?.name || "No property selected"}</p>
          {active?.address && <p className="text-xs text-muted-foreground">{active.address}</p>}
        </div>
        <div className="text-right text-xs text-muted-foreground">
          Menu, stock, orders are all scoped to this hotel.
          <br />
          Change the property in the sidebar to switch POS.
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4"><p className="label-overline">Today's orders</p><p className="font-display text-3xl mt-1">{summary.orders_count}</p></Card>
        <Card className="p-4"><p className="label-overline">Gross sales</p><p className="font-display text-3xl mt-1">{inr(summary.gross_total)}</p></Card>
        <Card className="p-4"><p className="label-overline">GST collected</p><p className="font-display text-3xl mt-1 text-secondary">{inr(summary.gst_total)}</p></Card>
        <Card className="p-4"><p className="label-overline">Low stock</p><p className="font-display text-3xl mt-1 text-[hsl(var(--warning))]">{summary.low_stock?.length || 0}</p></Card>
      </div>

      {summary.low_stock?.length > 0 && (
        <Card className="p-3 mb-4 border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 flex gap-2 items-center">
          <WarningIcon size={16} className="text-[hsl(var(--warning))]" />
          <p className="text-sm">
            <strong>{summary.low_stock.length}</strong> item{summary.low_stock.length === 1 ? "" : "s"} running low:
            <span className="ml-1 text-muted-foreground">{summary.low_stock.slice(0, 4).map((x) => `${x.name} (${x.stock})`).join(", ")}</span>
          </p>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="tables" data-testid="pos-tab-tables"><TableIcon size={14} className="mr-1" />Dine-in tables</TabsTrigger>
          <TabsTrigger value="order" data-testid="pos-tab-order"><ReceiptIcon size={14} className="mr-1" />Quick order</TabsTrigger>
          <TabsTrigger value="history" data-testid="pos-tab-history"><PrinterIcon size={14} className="mr-1" />Today's orders</TabsTrigger>
          <TabsTrigger value="bills" data-testid="pos-tab-bills"><ReceiptIcon size={14} className="mr-1" />Bills · reprint</TabsTrigger>
          <TabsTrigger value="menu" data-testid="pos-tab-menu"><ForkKnifeIcon size={14} className="mr-1" />Menu & stock</TabsTrigger>
          <TabsTrigger value="stock" data-testid="pos-tab-stock"><PackageIcon size={14} className="mr-1" />Store & stock</TabsTrigger>
          <TabsTrigger value="qr" data-testid="pos-tab-qr"><QrCodeIcon size={14} className="mr-1" />Menu QR</TabsTrigger>
        </TabsList>

        <TabsContent value="tables">
          <TablesFloor
            activeId={activeId}
            refreshKey={tablesRefreshKey}
            onTableClick={(to) => setActiveTable(to)}
          />
        </TabsContent>

        <TabsContent value="order" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Menu grid */}
          <div className="lg:col-span-2 space-y-4">
            <div className="relative">
              <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
                placeholder="Search menu — name, SKU or category…"
                className="w-full pl-9 pr-3 h-10 rounded-md border border-input bg-transparent text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                data-testid="pos-order-search"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCatFilter(c)}
                  className={`px-3 py-1 rounded-full text-xs uppercase tracking-wider border transition-colors ${catFilter === c ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}
                  data-testid={`pos-cat-${c}`}
                >
                  {c === "all" ? `All (${menu.length})` : c}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {filteredMenu.filter((m) => m.is_active).map((mi) => {
                const Icon = CATEGORY_ICON[mi.category] || PackageIcon;
                const outOfStock = isOutOfStock(mi);
                const pegs = remainingPegs(mi);
                return (
                  <button
                    key={mi.id}
                    onClick={() => addToCart(mi)}
                    disabled={outOfStock}
                    className={`text-left p-3 rounded-lg border transition-all ${outOfStock ? "opacity-40 cursor-not-allowed" : "hover:border-primary hover:shadow-md"} border-border bg-card`}
                    data-testid={`pos-menu-item-${mi.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <Icon size={22} weight="duotone" className="text-primary" />
                      <Badge variant="outline" className={CATEGORY_TONE[mi.category] || ""}>{mi.category}</Badge>
                    </div>
                    <p className="font-medium mt-2 text-sm">{mi.name}</p>
                    {mi.unit_type === "peg" && (
                      <p className="text-[10px] text-muted-foreground">{mi.peg_size_ml}ml peg · from {mi.bottle_size_ml}ml bottle</p>
                    )}
                    <div className="flex items-baseline justify-between mt-1">
                      <p className="font-display text-lg">{inr(mi.price)}{mi.unit_type === "peg" && <span className="text-[10px] text-muted-foreground"> /peg</span>}</p>
                      <p className="text-[10px] text-muted-foreground">{mi.gst_rate}% GST</p>
                    </div>
                    {mi.track_stock && (
                      mi.unit_type === "peg" ? (
                        <p className={`text-[10px] mt-1 ${pegs < 3 ? "text-[hsl(var(--warning))]" : "text-muted-foreground"}`}>Stock: {pegs} pegs · {mi.stock}ml left</p>
                      ) : (
                        <p className={`text-[10px] mt-1 ${mi.stock < 5 ? "text-[hsl(var(--warning))]" : "text-muted-foreground"}`}>Stock: {mi.stock}</p>
                      )
                    )}
                  </button>
                );
              })}
              {filteredMenu.length === 0 && (
                <div className="col-span-full text-center text-muted-foreground py-10 text-sm">
                  <ForkKnifeIcon size={24} className="mx-auto mb-2 opacity-40" />
                  No menu items yet. Add them from the "Menu & stock" tab.
                </div>
              )}
            </div>
          </div>

          <PosCart
            cart={cart}
            updateQty={updateQty}
            removeLine={removeLine}
            orderType={orderType}
            setOrderType={setOrderType}
            paymentMode={paymentMode}
            setPaymentMode={setPaymentMode}
            discount={discount}
            setDiscount={setDiscount}
            discountType={discountType}
            setDiscountType={setDiscountType}
            tableNo={tableNo}
            setTableNo={setTableNo}
            customer={customer}
            setCustomer={setCustomer}
            occupiedRooms={occupiedRooms}
            selectedRoomId={selectedRoomId}
            setSelectedRoomId={setSelectedRoomId}
            openTables={openTables}
            selectedTableOrderId={selectedTableOrderId}
            setSelectedTableOrderId={setSelectedTableOrderId}
            totals={totals}
            submitOrder={submitOrder}
          />
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr className="text-left">
                  <th className="p-3 font-medium">Order</th>
                  <th className="p-3 font-medium">Time</th>
                  <th className="p-3 font-medium">Table</th>
                  <th className="p-3 font-medium">Items</th>
                  <th className="p-3 font-medium">Payment</th>
                  <th className="p-3 font-medium text-right">Tax</th>
                  <th className="p-3 font-medium text-right">Total</th>
                  {canDeleteBills && <th className="p-3 font-medium text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const isBilled = !!o.billed_invoice_id;
                  return (
                    <tr key={o.id} className="border-b border-border/50 last:border-b-0" data-testid={`pos-order-row-${o.id}`}>
                      <td className="p-3 font-mono-plex text-xs">{o.order_no}</td>
                      <td className="p-3 text-xs text-muted-foreground">{new Date(o.created_at).toLocaleTimeString("en-IN")}</td>
                      <td className="p-3">{o.table_no || "—"}</td>
                      <td className="p-3 text-xs">{o.items?.length || 0} items</td>
                    <td className="p-3 text-xs uppercase" data-testid={`pos-order-payment-${o.id}`}>
                      {o.payment_mode?.replace("_", " ")}
                      {(o.payment_mode === "room_charge" || o.order_type === "room_service") && o.room_number && (
                        <span className="ml-1 font-mono-plex text-primary normal-case tracking-wider">{" · Room "}{o.room_number}</span>
                      )}
                      {isBilled && <Badge variant="outline" className="ml-2 text-[9px]">BILLED</Badge>}
                    </td>
                      <td className="p-3 text-right font-mono-plex text-xs">{inr(o.tax)}</td>
                      <td className="p-3 text-right font-mono-plex">{inr(o.total)}</td>
                      {canDeleteBills && (
                        <td className="p-3 text-right">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => deleteOrder(o)}
                            disabled={isBilled}
                            title={isBilled ? "Already billed — void the invoice first" : "Void this POS order"}
                            data-testid={`pos-order-delete-${o.id}`}
                          >
                            <TrashIcon size={14} className={isBilled ? "text-muted-foreground" : "text-destructive"} />
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {orders.length === 0 && <tr><td colSpan={canDeleteBills ? 8 : 7} className="text-center py-12 text-muted-foreground text-sm">No orders yet today.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="menu">
          <MenuManager activeId={activeId} menu={menu} canManage={canManage} onChanged={loadMenu} />
        </TabsContent>

        <TabsContent value="bills">
          <TableHistory activeId={activeId} />
        </TabsContent>

        <TabsContent value="stock">
          <StockManager activeId={activeId} menu={menu} onChanged={() => { loadMenu(); loadSummary(); }} />
        </TabsContent>

        <TabsContent value="qr">
          <MenuQR activeId={activeId} active={active} />
        </TabsContent>
      </Tabs>

      {activeTable && (
        <TableOrderPanel
          activeId={activeId}
          tableOrder={activeTable}
          menu={menu}
          platform={platform}
          onClose={() => { setActiveTable(null); setTablesRefreshKey((k) => k + 1); loadMenu(); loadOrders(); loadSummary(); loadOpenTables(); }}
          onChange={() => { setTablesRefreshKey((k) => k + 1); loadOpenTables(); }}
        />
      )}
    </div>
  );
}
